import Axios from "axios";
import { REACT_APP_API_URL } from "../../config/api-config";

const axios = Axios.create({
  baseURL: `${REACT_APP_API_URL}`,
});
// ⚠️ TEMPORARY - For development only
axios.defaults.headers.common['Access-Control-Allow-Mixed-Content'] = true;
/**
 * ✅ IMPROVED: Request interceptor with robust token extraction
 * Handles multiple token storage formats and validates JWT
 */
axios.interceptors.request.use(
  (config) => {
    console.log("=== 🔵 AXIOS REQUEST INTERCEPTOR ===");
    console.log("📡 Request URL:", config.url);
    console.log("📨 Request Method:", config.method.toUpperCase());
    
    // ✅ Check ALL possible token locations in localStorage
    const possibleKeys = [
      "access_token",
      "accessToken", 
      "token",
      "auth_token",
      "Bearer",
      "userData",
      "user",
      "tokenData"
    ];
    
    let accessToken = null;
    let foundKey = null;
    
    // ✅ Try each possible key to find valid JWT token
    for (let key of possibleKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        // Extract JWT token using regex (JWT format: eyJ...eyJ...eyJ)
        const jwtMatch = value.match(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/);
        
        if (jwtMatch) {
          accessToken = jwtMatch[0];
          foundKey = key;
          console.log(`✅ Found valid JWT token in localStorage.${foundKey}`);
          break;
        }
      }
    }
    
    // ✅ Add Authorization header if token found
    if (accessToken) {
      const tokenPreview = accessToken.substring(0, 20) + "...";
      config.headers.Authorization = `Bearer ${accessToken}`;
      console.log(`🟢 Authorization header added: Bearer ${tokenPreview}`);
      console.log(`📍 Source: localStorage.${foundKey}`);
    } else {
      console.warn("⚠️ WARNING: NO TOKEN FOUND IN LOCALSTORAGE");
      console.warn("📋 Available localStorage keys:", Object.keys(localStorage));
      console.warn("⚠️ Request will be sent WITHOUT authentication");
    }
    
    console.log("✅ Final Authorization:", config.headers.Authorization ? "SET" : "NOT SET");
    console.log("=== END REQUEST INTERCEPTOR ===\n");
    
    return Promise.resolve(config);
  },
  (error) => {
    console.error("❌ REQUEST INTERCEPTOR ERROR:", error);
    return Promise.reject(error);
  }
);

/**
 * ✅ IMPROVED: Response interceptor with better error handling
 * Provides detailed error messages for different HTTP status codes
 */
axios.interceptors.response.use(
  (response) => {
    const status = response.status;
    const method = response.config.method.toUpperCase();
    const url = response.config.url;
    
    console.log(`✅ ${status} ${method} ${url}`);
    return Promise.resolve(response);
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url;
    const method = error?.config?.method?.toUpperCase();
    
    console.error(`\n❌ API ERROR: ${status} ${method} ${url}`);
    
    // ✅ Handle 401 Unauthorized (Invalid/Expired Token)
    if (status === 401) {
      console.error("❌ 401 UNAUTHORIZED");
      console.error("🔐 Reason: Token is invalid, expired, or missing");
      console.error("📋 Response:", error.response?.data);
      
      // Optional: Uncomment to auto-logout on token expiration
      // localStorage.clear();
      // window.location.href = "/app";
    }
    
    // ✅ Handle 403 Forbidden (No Permission)
    if (status === 403) {
      console.error("❌ 403 FORBIDDEN");
      console.error("🔐 Reason: User doesn't have permission for this action");
      console.error("📋 Response:", error.response?.data);
    }
    
    // ✅ Handle 404 Not Found
    if (status === 404) {
      console.warn("⚠️ 404 NOT FOUND");
      console.warn("📍 Endpoint:", url);
      console.warn("💡 Tip: Check if the API endpoint exists");
    }
    
    // ✅ Handle 500 Server Error
    if (status === 500) {
      console.error("❌ 500 SERVER ERROR");
      console.error("🖥️ Backend encountered an error");
      console.error("📋 Response:", error.response?.data);
    }
    
    // ✅ Handle Network Errors (no response)
    if (!error.response) {
      console.error("❌ NETWORK ERROR");
      console.error("🌐 Possible causes:");
      console.error("   - Backend server is down");
      console.error("   - CORS issue");
      console.error("   - Network connectivity problem");
      console.error("📋 Error:", error.message);
    }
    
    return Promise.reject(error);
  }
);

export default axios;